import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image/Image'
import NestableClickable from 'components/Common/NestableClickable'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LuxPlusHiddenOfferDetails from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferDetails'
import LuxPlusHiddenOfferImageOverlay from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferImageOverlay'
import useHiddenOfferClickHandler from 'luxPlus/components/HiddenOffer/useHiddenOfferClickHandler'
import getResizableImage from 'components/OfferList/OfferCards/getResizableImage'
import { LabelCountProvider } from 'contexts/LabelCountContext'
import { COMPACT_HOTEL_CARD_WIDTH } from './CarouselOfferCardStyles'
import { Card, Hero, Content } from './ChatHotelOfferCardStyles'

const LockedCard = styled(NestableClickable)`
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.L};
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
}

function ChatLockedHotelOfferCard({ offer }: Props) {
  const intl = useIntl()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])
  const handleUnlock = useHiddenOfferClickHandler(offer)
  const image = useMemo(() => getResizableImage(offer.image), [offer.image])

  const unlockLabel = intl.formatMessage({
    id: 'cF0wqDB2oTUCvbESRvph',
    defaultMessage: 'Unlock this offer via Free Preview',
    description: 'CTA button text to unlock a hidden offer by starting LuxPlus+ free preview',
  })

  return <ProductPaletteProvider product={offer}>
    <LockedCard onClick={handleUnlock} aria-label={unlockLabel}>
      <Card background="normal-background" border="light-foreground" borderRadius="L" data-testid="chat-hotel-offer-card">
        <Group direction="vertical" fullHeight>
          <Hero>
            <Image image={image} aspectRatio="16:9" width={COMPACT_HOTEL_CARD_WIDTH} fit="center" quality="low" alt="" onLoad={handleImageLoaded} />
            <LuxPlusHiddenOfferImageOverlay />
          </Hero>
          <Content padding="16 16 8">
            <LabelCountProvider maxLabels={2}>
              <LuxPlusHiddenOfferDetails offer={offer} />
            </LabelCountProvider>
          </Content>
        </Group>
      </Card>
    </LockedCard>
  </ProductPaletteProvider>
}

export default ChatLockedHotelOfferCard
